import I18n from 'I18n';
import unescapedText from 'I18n/utils/unescapedText';
import { useSearchParams } from 'marketplace-ui-common/hooks/useSearchParams';
import { interpolate } from 'marketplace-ui-common/i18n/utils';
import { useMemo } from 'react';
import { useLocation } from 'quartz-routing/react-router';
import { sanitize } from 'sanitize-text/sanitizers/Sanitize';
import { Paths } from '../../constants/paths';
import {
  AppliedFilter,
  useAppliedFilters,
} from '../../filters/hooks/useAppliedFilters';
import { useFilterConfig } from '../../filters/hooks/useFilterConfig';
import { useProductTypeSearchParam } from '../../hooks/useProductTypeSearchParams';
import { useCollectionQuery } from './useCollectionQuery';
import { useSearchResultsQuery } from './useSearchResultsQuery';

export interface SearchResultsPageMetadata {
  title: string;
  heading: string;
  metaDescription: string;
}

export function useSearchResultsPageMetadata(): SearchResultsPageMetadata {
  const { pathname } = useLocation();
  const collectionResult = useCollectionQuery();
  const productType = useProductTypeSearchParam();
  const searchParams = useSearchParams();
  const appliedFilters = useAppliedFilters();
  const filterConfig = useFilterConfig();
  const { data } = useSearchResultsQuery();
  const searchTerm = searchParams.get('search');

  const count = data?.result.total ?? 0;
  const translationCount = count > 0 ? count : 1;
  const formattedCount = I18n.formatNumber(count);

  return useMemo(() => {
    if (collectionResult.data && !collectionResult.isUmbrellaCollection) {
      return {
        heading: interpolate(
          collectionResult.data.title ?? collectionResult.data.name,
          { count: formattedCount }
        ),
        metaDescription: collectionResult.data.metaDescription,
        title: interpolate(collectionResult.data.htmlPageTitle, {
          count: formattedCount,
        }),
      };
    }

    if (searchTerm) {
      return {
        title: unescapedText(
          'marketplaceStorefront.documentHead.searchResults.defaultWithSearch.title',
          { searchTerm: sanitize(searchTerm) }
        ),
        heading: unescapedText('marketplaceStorefront.headers.searchResults'),
        metaDescription: unescapedText(
          'marketplaceStorefront.documentHead.searchResults.defaultWithSearch.metaDescription'
        ),
      };
    }

    // App product type handling
    if (productType === 'APP') {
      // Get category filter for apps
      const categoryFilter = appliedFilters.find(
        (f: AppliedFilter) => f.field === 'CATEGORY'
      );
      const singleCategory =
        categoryFilter?.values?.length === 1 ? categoryFilter.values[0] : null;

      // App with single category
      const categoryOption = filterConfig.CATEGORY?.options?.find(
        (opt) => singleCategory && opt.value === singleCategory
      );

      // App without category or multiple categories
      if (!categoryOption) {
        return {
          title: unescapedText(
            'marketplaceStorefront.documentHead.searchResults.apps.title',
            { count: translationCount, formattedCount }
          ),
          heading: unescapedText(
            'marketplaceStorefront.searchResults.header.allApps'
          ),
          metaDescription: unescapedText(
            'marketplaceStorefront.documentHead.searchResults.apps.metaDescription'
          ),
        };
      }

      const categoryName = categoryOption.text;

      return {
        title: unescapedText(
          'marketplaceStorefront.documentHead.searchResults.apps.categoryTitle',
          { count: translationCount, formattedCount, category: categoryName }
        ),
        heading: unescapedText(
          'marketplaceStorefront.documentHead.searchResults.apps.categoryHeading',
          { category: categoryName }
        ),
        metaDescription: unescapedText(
          'marketplaceStorefront.documentHead.searchResults.apps.categoryMetaDescription',
          { category: categoryName }
        ),
      };
    }

    // Agent product type handling
    if (productType === 'AGENT') {
      // Get team filter for agents
      const teamFilter = appliedFilters.find(
        (f: AppliedFilter) => f.field === 'AGENT_TEAM'
      );
      const singleTeam =
        teamFilter?.values?.length === 1 ? teamFilter.values[0] : null;

      // Agent with single team
      const teamOption = filterConfig.AGENT_TEAM?.options?.find(
        (opt) => singleTeam && opt.value === singleTeam
      );

      // Agent without team or multiple teams
      if (!teamOption) {
        return {
          title: unescapedText(
            'marketplaceStorefront.documentHead.searchResults.agents.title',
            { count: translationCount, formattedCount }
          ),
          heading: unescapedText(
            'marketplaceStorefront.searchResults.header.allAgents'
          ),
          metaDescription: unescapedText(
            'marketplaceStorefront.documentHead.searchResults.agents.metaDescription'
          ),
        };
      }

      const teamName = teamOption?.text || singleTeam;

      return {
        title: unescapedText(
          'marketplaceStorefront.documentHead.searchResults.agents.teamTitle',
          { count: translationCount, formattedCount, team: teamName }
        ),
        heading: unescapedText(
          'marketplaceStorefront.documentHead.searchResults.agents.teamHeading',
          { team: teamName }
        ),
        metaDescription: unescapedText(
          'marketplaceStorefront.documentHead.searchResults.agents.teamMetaDescription',
          { team: teamName }
        ),
      };
    }

    if (productType === 'WORKFLOW_TEMPLATE') {
      return {
        title: unescapedText(
          'marketplaceStorefront.documentHead.workflowTemplates.title'
        ),
        heading: unescapedText(
          'marketplaceStorefront.searchResults.header.allWorkflowTemplates'
        ),
        metaDescription: unescapedText(
          'marketplaceStorefront.documentHead.workflowTemplates.metaDescription'
        ),
      };
    }

    if (pathname === Paths.SEARCH_RESULTS_SOLUTIONS_PARTNERS) {
      return {
        title: unescapedText(
          'marketplaceStorefront.documentHead.solutions.title'
        ),
        heading: unescapedText(
          'marketplaceStorefront.searchResults.header.allSolutionsPartners'
        ),
        metaDescription: unescapedText(
          'marketplaceStorefront.documentHead.solutions.metaDescription'
        ),
      };
    }

    return {
      title: unescapedText(
        'marketplaceStorefront.documentHead.searchResults.default.title',
        { count: translationCount, formattedCount }
      ),
      heading: unescapedText(
        'marketplaceStorefront.searchResults.header.allProducts'
      ),
      metaDescription: unescapedText(
        'marketplaceStorefront.documentHead.searchResults.default.metaDescription'
      ),
    };
  }, [
    appliedFilters,
    collectionResult.data,
    collectionResult.isUmbrellaCollection,
    filterConfig.AGENT_TEAM?.options,
    filterConfig.CATEGORY?.options,
    formattedCount,
    pathname,
    productType,
    searchTerm,
    translationCount,
  ]);
}
